import { Program } from './program';
import { sprintf } from 'sprintf-js';

/**
 * This is a helper class for converting weekly programs between UTC and local time.
 */
export class WeeklyProgram extends Program {
	/**
	 * @param time {string} Time as string, e.g. '13:35'
	 * @param days {string[]} List of days
	 * @param [offset] {int} Timezone offset in minutes. Default 0 = UTC. CEST = -120
	 */
	constructor( time, days, offset ) {
		super( offset );
		this.time = time;
		this.days = days;
	}


	/**
	 * Returns a WeeklyProgram, parsed from timestamp and days
	 * @param apiWeeklyProgram { {timestamp: string, days: string[]} }
	 */
	static fromApi( apiWeeklyProgram ) {
		const days = daysFromApi( apiWeeklyProgram.days )

		if(apiWeeklyProgram.timestamp) {
			const{time, offset} = parseTimestamp( apiWeeklyProgram.timestamp ) 
			return new WeeklyProgram( time, days, offset );
		} 

		//Timestamp was not available. Use time and offset
		const time = apiWeeklyProgram.time
		const offset = new Date().getTimezoneOffset();
		
		return new WeeklyProgram( time, days, offset );
	}

	/**
	 * Returns a WeeklyProgram, parsed from time, days and offset
	 * @param apiWeeklyProgram { {time: string, days: string[]} }
	 * @param offset {int} Timezone offset in minutes. 0 = UTC. CEST = -120 for the 'time'
	 *               given in apiWeeklyProgram
	 */
	static fromTimeAndOffset( apiWeeklyProgram, offset ) {
		const days = daysFromApi( apiWeeklyProgram.days )

		return new WeeklyProgram( apiWeeklyProgram.time, days, offset );
	}

	/**
	 * Returns a list of Days, converted from WeeklyProgram days to API format
	 * @returns {days:[String]}
	 */
	getApiDays() {
		return daysToApi(this.days);
	}

	/**
	 * Returns a timestamp string, based on time and offset
	 * @returns {String}, "hh:mm(+/-)hh:mm" or "hh:mmZ"
	 */
	getTimestamp() {
		let timestamp = this.time;
		let timezone = this.offset / (-60);

		if(timezone == 0) {
			return timestamp + 'Z';
		}

		let sign = timezone < 0 ? '-' : '+';
		let absTimezone = Math.abs(timezone);
		let timezoneString = joinTime(absTimezone, 0);
		
		return timestamp + sign + timezoneString;
	}

	/**
	 * Returns a copy shifted to the specified offset.
	 * This can change the days, if the time crosses 00:00 when converted
	 * @param offset {int} Timezone offset in minutes. 0 = UTC. CEST = -120.
	 * @returns {WeeklyProgram}
	 */
	toOffset( offset ) {
		let { hours, minutes } = splitTime( this.time );
		const offsetDiff       = this.offset - offset;
		let dayOffset          = 0;
		minutes += offsetDiff;
		
		while ( minutes >= 60 ) {
			minutes -= 60;
			hours++;
		}
		
		while ( minutes < 0 ) {
			minutes += 60;
			hours--;
		}
		while ( hours >= 24 ) {
			hours -= 24;
			dayOffset++;
		}
		while ( hours < 0 ) {
			hours += 24;
			dayOffset--;
		}
		
		let shiftedDays = shiftDays( this.days, dayOffset );
		
		shiftedDays.sort( ( a, b ) => days.indexOf( a ) - days.indexOf( b ) );
		
		return new WeeklyProgram( joinTime(  hours, minutes  ), shiftedDays, offset );
	}
	
	
	
	/**
	 * Get next time and day
	 * @param programs {WeeklyProgram[]}
	 * @param [now] {Date} Date to base calculation on. Default is new Date()
	 * @return { ?{program : WeeklyProgram, day : string, totalMinutesUntil : number}}
	 */
	static nextTime( programs, now ) {
		if ( 0 === programs.length ) {
			return null;
		}
		now                = now || new Date();
		let nowDayIndex    = ( now.getUTCDay() + 7 - 1 ) % 7; // JS monday = 1. In our days array is monday=0
		let nowMinuteOfDay = now.getUTCHours() * 60 + now.getUTCMinutes();
		
		let sortedPrograms = flatten(
			programs
				.map( p => p.toUTC() )
				.map( program => {
					let { hours, minutes } = splitTime( program.time );
					return program.days.map( day => {
						return { dayIndex: days.indexOf( day ), minuteOfDay: hours * 60 + minutes, program };
					} );
				} )
		)
			.map( ( { dayIndex, minuteOfDay, program } ) => {
				let daysUntil         = ( dayIndex - nowDayIndex + 7 ) % 7;
				let totalMinutesUntil = ( ( daysUntil * minutesPerDay + ( minuteOfDay - nowMinuteOfDay ) ) + minutesPerWeek ) % minutesPerWeek;
				return { program, day: days[ dayIndex ], totalMinutesUntil };
			} )
			.sort( ( a, b ) => a.totalMinutesUntil - b.totalMinutesUntil );
		return sortedPrograms[ 0 ];
	}
	
	/** @inheritDoc */
	nextOccurrence( now ) {
		now                = now || new Date();
		let nowDayIndex    = ( now.getUTCDay() + 7 - 1 ) % 7; // JS monday = 1. In our days array is monday=0
		let nowMinuteOfDay = now.getUTCHours() * 60 + now.getUTCMinutes();
		
		const inUtc = this.toUTC();
		let { hours, minutes } = splitTime( inUtc.time );
		if(this.days.length > 0) {
			let upcomingTimes      = this.days.map(
				day => {
					const dayIndex        = days.indexOf( day );
					const minuteOfDay     = hours * 60 + minutes;
					let daysUntil         = ( dayIndex - nowDayIndex + 7 ) % 7;
					let totalMinutesUntil = ( ( daysUntil * minutesPerDay + ( minuteOfDay - nowMinuteOfDay ) ) + minutesPerWeek ) % minutesPerWeek;
					
					let newDate = new Date( now.getTime() + 1000 * 60 * totalMinutesUntil );
					newDate.setSeconds( 0, 0 );
					return newDate;
				}
			);

		upcomingTimes.sort((a,b) => a - b);
		return upcomingTimes[0];
		} else {
			//Now days marked means event is today
			const minuteOfDay     = hours * 60 + minutes;
			let totalMinutesUntil = ( ( minuteOfDay - nowMinuteOfDay )  + minutesPerWeek ) % minutesPerWeek;
					
			let newDate = new Date( now.getTime() + 1000 * 60 * totalMinutesUntil );
			newDate.setSeconds( 0, 0 );
			return newDate;
		}
	}
}

/**
 * These are the days. All of them.
 * @type {string[]}
 */
const days = [ 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday' ];

const minutesPerDay  = 24 * 60;
const minutesPerWeek = 7 * minutesPerDay;


/**
 * Splits a time to {hours, minutes}
 * @param timeString
 * @returns {{hours : number, minutes : number}}
 */
function splitTime( timeString ) {
	let [ hours, minutes ] = timeString.split( ':' );
	return { hours: parseInt( hours ), minutes: parseInt( minutes ) };
}

/**
 * Formats hours and minutes to time
 * @param hours {number}
 * @param minutes {number}
 */
function joinTime( hours, minutes ) {
	return sprintf( '%02f:%02f', hours, minutes );
}

/**
 * @param day {string} A day
 * @param offset {number} The day should be shifted this number of days
 * @returns {string} The shifted day
 */
function shiftDay( day, offset ) {
	let oldIndex = days.indexOf( day );
	return days[ ( oldIndex + offset + 7 ) % 7 ];
}

/**
 *
 * @param days {string[]} Array of days
 * @param offset {number} All days should be shifted this number of days
 * @returns {string[]} Shifted array of days
 */
function shiftDays( days, offset ) {
	return days.map( day => shiftDay( day, offset ) );
}

/**
 * Helper, as we do not have flatMap yet
 
 * @param {T[][]} arrayOfArrays
 * @return {T[]}
 * @template T
 */
function flatten( arrayOfArrays ) {
	let result = [];
	arrayOfArrays.forEach( array => array.map( item => result.push( item ) ) );
	return result;
}


/**
 * Converts API day format to WeeklyProgram day format
 
 * @param knownDays [String] array of days in uppercase english
 * @return {[String]} array of days in lowercase english
 */
function daysFromApi( knownDays ) {
	return knownDays.map( upperCaseDay => upperCaseDay.toLowerCase());
}

/**
 * Converts WeeklyProgram day format to API day format
 
 * @param days [String] array of days in lowercase english
 * @return {[String]} array of days in uppercase english
 */
function daysToApi( days ) {
	return days.map( lowerCaseDay => lowerCaseDay.toUpperCase());
}


/**
 * Parses a timestamp to a time and an offset, without changing the date
 *
 * @param timestamp Timestamp on the form "hh:mm(+/-)hh:mm" or "hh:mmZ"
 * @return {time:String, offset:Int} 
 * time Time on the form "hh:mm" 
 * offset Timezone offset in minutes. 0 = UTC. CEST = -120.
 */
function parseTimestamp( timestamp ) {
	// timestamp = "hh:mmZ", where Z is either just 'Z' (GMT) or (-+)TT:TT
	// Use a dummyDate to parse out valid time and offset

	let dummyDay = 2;
	let dummyDateString = '2000-01-02T';
	let dummyDate = new Date(dummyDateString + timestamp);

	let day = dummyDate.getDate();
	let hour = dummyDate.getHours();
	let offset = dummyDate.getTimezoneOffset();


	//If date has changed, shift time back to original day
	if( day > dummyDay ) {
		//day has been shifted forward. Convert hours and offset to set time back over midnight to 23:00
		let shiftedHour = 23                          // Will shift time to hour 23 (the previous day)  
		let hoursToMidnight = 24 - shiftedHour;       // Hours from shifted time to midnight
		let hoursToShift = hoursToMidnight + hour;    // Hours from shifted hour to the current hour
		let shiftOffset = hoursToShift * (-60);       // The offset that is needed to shift time backwards

		offset = offset - shiftOffset;                // Shift offset backwards
		hour = shiftedHour;                           // Shift hours backwards
	}

	if( day < dummyDay ){
		//Day has been shifted backwards. Convert hours and offset to convert time forward over midnight to 01:00
		let shiftedHour = 1;                           //  Will shift time to hour 1 past midnight 
		let hoursToShift = (24 + shiftedHour) - hour;  // Time difference in hours from hour to next day 01:00
		let shiftOffset = hoursToShift * (-60)         // The offset that is needed to shift time forward

		offset = offset + shiftOffset;                 // Shift offset forwards
		hour = shiftedHour;                            // Shift hours forward 
	}
	
	let minute = dummyDate.getMinutes();
	let time = joinTime(hour, minute);

	return {time, offset};
}